const defaults = {
  color: {
    background: '#ffffff', surface: '#ffffff', surfaceAlt: '#f5f7fa', text: '#111317', muted: '#5d6672', accent: '#1769aa', accentText: '#ffffff', border: '#dfe4ea'
  },
  typography: { fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', headingWeight: 720, bodyWeight: 400 },
  layout: { maxWidth: '1180px', contentWidth: '760px', sectionSpace: 'clamp(3.5rem, 8vw, 7rem)' },
  radius: { small: '8px', medium: '14px', large: '24px' },
  shadow: { card: '0 18px 60px rgba(17, 19, 23, 0.08)' }
};

export const THEME_PRESETS = [
  {
    key: 'professional-clean',
    name: 'Professional Clean',
    description: 'Bright, simple, and familiar for professional services.',
    tokens: defaults
  },
  {
    key: 'deep-navy',
    name: 'Deep Navy',
    description: 'A confident dark-first look with strong contrast.',
    tokens: {
      color: { background: '#07111f', surface: '#0f1b2d', surfaceAlt: '#111f33', text: '#f8fafc', muted: '#c7d2df', accent: '#47a3ff', accentText: '#06101d', border: '#26374d' },
      typography: defaults.typography,
      layout: defaults.layout,
      radius: defaults.radius,
      shadow: { card: '0 22px 70px rgba(0, 0, 0, 0.28)' }
    }
  },
  {
    key: 'warm-trust',
    name: 'Warm Trust',
    description: 'Soft cream surfaces with a calm blue accent.',
    tokens: {
      color: { background: '#fffaf2', surface: '#ffffff', surfaceAlt: '#f5ecdf', text: '#211a13', muted: '#6d6256', accent: '#245f89', accentText: '#ffffff', border: '#e4d7c7' },
      typography: defaults.typography,
      layout: defaults.layout,
      radius: { small: '10px', medium: '18px', large: '30px' },
      shadow: { card: '0 18px 55px rgba(78, 58, 31, 0.10)' }
    }
  },
  {
    key: 'modern-slate',
    name: 'Modern Slate',
    description: 'Muted slate palette with crisp, contemporary spacing.',
    tokens: {
      color: { background: '#f7f9fb', surface: '#ffffff', surfaceAlt: '#e9eef4', text: '#101828', muted: '#566273', accent: '#334bc4', accentText: '#ffffff', border: '#d5dce7' },
      typography: defaults.typography,
      layout: { maxWidth: '1220px', contentWidth: '780px', sectionSpace: 'clamp(4rem, 9vw, 8rem)' },
      radius: { small: '6px', medium: '12px', large: '20px' },
      shadow: { card: '0 18px 50px rgba(16, 24, 40, 0.09)' }
    }
  },
  {
    key: 'classic-paper',
    name: 'Classic Paper',
    description: 'Editorial and traditional, with understated contrast.',
    tokens: {
      color: { background: '#fbfaf7', surface: '#ffffff', surfaceAlt: '#efede8', text: '#161412', muted: '#625d56', accent: '#7d3f16', accentText: '#ffffff', border: '#ded9d1' },
      typography: { fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif', headingWeight: 700, bodyWeight: 400 },
      layout: { maxWidth: '1120px', contentWidth: '720px', sectionSpace: 'clamp(3.25rem, 7vw, 6.5rem)' },
      radius: { small: '4px', medium: '8px', large: '14px' },
      shadow: { card: '0 14px 40px rgba(55, 42, 28, 0.08)' }
    }
  }
];

function merge(base, override) {
  if (!override || typeof override !== 'object') return base;
  const result = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    result[key] = value && typeof value === 'object' && !Array.isArray(value) ? merge(base?.[key] ?? {}, value) : value;
  });
  return result;
}

export function resolveTheme(theme) {
  return merge(defaults, theme?.tokens ?? {});
}

export function getThemePreset(key) {
  return THEME_PRESETS.find((preset) => preset.key === key) ?? THEME_PRESETS[0];
}

export function createThemeFromPreset(key) {
  const preset = getThemePreset(key);
  return { presetKey: preset.key, tokens: merge({}, preset.tokens) };
}

export function applyThemePreset(theme, key) {
  const preset = getAvailableThemePreset(theme, key);
  return { ...(theme ?? {}), presetKey: preset.key, tokens: merge({}, preset.tokens) };
}

export function createCustomTheme(name, baseTokens = defaults) {
  const safeName = String(name || 'Custom Theme').trim() || 'Custom Theme';
  const slug = safeName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'custom-theme';
  const now = Date.now().toString(36);
  return {
    key: `custom-${slug}-${now}`,
    name: safeName,
    description: 'Custom theme created for this website.',
    isCustom: true,
    tokens: merge({}, baseTokens || defaults)
  };
}

export function getAvailableThemePresets(theme) {
  return [...THEME_PRESETS, ...((theme && Array.isArray(theme.customThemes)) ? theme.customThemes : [])];
}

export function getAvailableThemePreset(theme, key) {
  return getAvailableThemePresets(theme).find((preset) => preset.key === key) ?? getThemePreset(key);
}

export function themeToCssVariables(theme) {
  const resolved = resolveTheme(theme);
  return {
    '--sb-bg': resolved.color.background,
    '--sb-surface': resolved.color.surface,
    '--sb-surface-alt': resolved.color.surfaceAlt,
    '--sb-text': resolved.color.text,
    '--sb-muted': resolved.color.muted,
    '--sb-accent': resolved.color.accent,
    '--sb-accent-text': resolved.color.accentText,
    '--sb-border': resolved.color.border,
    '--sb-font': resolved.typography.fontFamily,
    '--sb-heading-weight': resolved.typography.headingWeight,
    '--sb-body-weight': resolved.typography.bodyWeight,
    '--sb-max-width': resolved.layout.maxWidth,
    '--sb-content-width': resolved.layout.contentWidth,
    '--sb-section-space': resolved.layout.sectionSpace,
    '--sb-radius-sm': resolved.radius.small,
    '--sb-radius-md': resolved.radius.medium,
    '--sb-radius-lg': resolved.radius.large,
    '--sb-card-shadow': resolved.shadow.card
  };
}
